import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction, deleteDocEverywhere, nodesUsingDoc } from '../../app/actions';
import { apiRequest } from '../../api/client';
import {
  DOC_TYPES,
  docKindForKey,
  docTypeDef,
  docIconId,
} from '../../lib/docs';
import { suggestDocSlug } from '../../lib/slug';
import type { DocType, NodeId } from '../../lib/types';
import { registerHandle } from '../../app/handles';
import { canEditAllDocs, canEditMap } from '../../lib/permissions';

/**
 * Choose, create or detach the documentation a node points at.
 *
 * ONE PAGE, MANY NODES. A doc page is not owned by the node it is attached to — several nodes
 * can point at the same page, which is why this is a picker over everything that exists
 * rather than a "create documentation" button. The search is over pages of the same KIND,
 * because the four slots are different kinds of thing and a Course is not a Demo.
 *
 * `custom` is a synthetic target rather than one of the four fixed slots: it appends to the
 * node's `customDocs` list instead of overwriting a field, so it has no "remove attached doc"
 * — there is no single thing to remove.
 *
 * THE TYPE IS CHOSEN, NEVER INFERRED. A page is written here, embedded from a URL, or a plain
 * link out. V0.21 guessed from the URL host and mis-read self-hosted Supademo domains, which
 * is why the choice is a control with a hint rather than a heuristic.
 */

export interface DocPickerHandle {
  open: (nodeId: NodeId, key: string) => void;
  close: () => void;
}

interface DocRow {
  id: string;
  title: string;
  kind: string;
  docType?: DocType;
  sourceUrl?: string | null;
}

/**
 * What a row says on its right.
 *
 * The slot kind for a written page; the host for one that points somewhere. When several
 * rows share a title, the host is the thing that tells them apart.
 */
function rowKind(d: DocRow): string {
  if (!d.sourceUrl) return d.kind;
  try {
    return new URL(d.sourceUrl).hostname.replace(/^www\./, '');
  } catch {
    return docTypeDef(d.docType).label;
  }
}

const isHttpUrl = (u: string): boolean => /^https?:\/\/\S+$/i.test(u.trim());

export function DocPicker({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [target, setTarget] = useState<{ nodeId: NodeId; key: string } | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<DocRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState<DocType>('standard');
  const [url, setUrl] = useState('');
  /** Whether the create form at the top of the list is open (V0.42.4). */
  const [creating, setCreating] = useState(false);
  /** The row currently asking "delete this everywhere?" — one at a time. */
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    host.classList.toggle('show', !!target);
  }, [target, host]);

  const api = useRef<DocPickerHandle>({ open: () => {}, close: () => {} });
  api.current.open = useCallback((nodeId: NodeId, key: string) => {
    setTarget({ nodeId, key });
    setSearch('');
    setQuery('');
    setRows(null);
    setFailed(false);
    setTitle('');
    setDocType('standard');
    setUrl('');
    setCreating(false);
    setConfirmId(null);
  }, []);
  api.current.close = useCallback(() => setTarget(null), []);
  useEffect(() => {
    registerHandle('docPicker', api.current);
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQuery(search), 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [search]);

  useEffect(() => {
    if (!target) return;
    let live = true;
    setFailed(false);
    // The locale matters: a page's title is per-language, and searching by title in the
    // wrong one finds nothing.
    const q = new URLSearchParams({ kind: docKindForKey(target.key), lang: s.currentLocale });
    if (query) q.set('search', query);
    void apiRequest<DocRow[]>(`/api/docs?${q.toString()}`).then(
      (d) => {
        if (!live) return;
        setRows(d || []);
        // The map's caches learn each page's type and URL, so docUrlFor() can build a link
        // to it the moment it is attached.
        for (const doc of d || []) callAction('noteDocMeta', doc.id, doc.docType, doc.sourceUrl);
      },
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [target, query, s.currentLocale]);

  if (!target) return null;

  const close = () => setTarget(null);
  const attach = (docId: string) => {
    callAction('attachDocToTarget', target.nodeId, target.key, docId, title.trim());
    close();
  };

  const create = async () => {
    const t = title.trim();
    if (!t) return alert('Enter a title for the new doc page.');
    if (docType !== 'standard' && !isHttpUrl(url)) {
      return alert('The URL must start with http:// or https://');
    }
    try {
      const doc = await apiRequest<{ id: string }>('/api/docs', {
        method: 'POST',
        body: {
          // A SUGGESTION. The server generates the real one — it folds diacritics and
          // resolves collisions (overview, overview-2, …), and taking a client slug verbatim
          // is what made two documents with the same title a 500.
          slug: suggestDocSlug(t),
          kind: docKindForKey(target.key),
          locale: s.currentLocale,
          title: t,
          docType,
          sourceUrl: docType === 'standard' ? undefined : url.trim(),
        },
      });
      // Type and URL BEFORE attaching: attaching fills the node's link slot through
      // docUrlFor(), which reads them.
      callAction('noteDocMeta', doc.id, docType, docType === 'standard' ? null : url.trim());
      callAction('attachDocToTarget', target.nodeId, target.key, doc.id, t);
      close();
      // A written page opens straight into the editor — there is nothing to read yet.
      callAction('openDocEditor', doc.id, target.nodeId);
    } catch {
      alert('Could not create the doc page.');
    }
  };

  /**
   * How many nodes point at this page — the number the confirmation needs (V0.42.4).
   *
   * ONE PAGE, MANY NODES is this dialog's whole premise, so "delete" is never local: it takes
   * the page from every node holding it. The count is read from the map the client already
   * has (the four fixed slots plus `customDocs`), which is the same place `forgetDoc()` clears
   * it from afterwards — no second source, and no request to ask the question.
   */
  const usedBy = (docId: string): number => nodesUsingDoc(docId);

  /**
   * Delete the page itself — NOT the same act as the button at the bottom of this dialog,
   * which detaches and leaves the page alive for every other node using it. Same route and
   * same rule as the document editor's Delete (`EDIT_MAP` or `EDIT_ALL_DOCS`), and the same
   * local mirror afterwards, so there is one implementation of "this page is gone".
   */
  const remove = async (docId: string) => {
    if (await deleteDocEverywhere(docId)) {
      setRows((prev) => (prev ? prev.filter((r) => r.id !== docId) : prev));
      setConfirmId(null);
    }
  };

  const detach = () => {
    callAction('detachDocFromTarget', target.nodeId, target.key);
    close();
  };

  const typeDef = docTypeDef(docType);
  // The server refuses everyone else, so the control is not drawn rather than drawn dead.
  const mayDelete = canEditMap(s.perm) || canEditAllDocs(s.perm);

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-doc" />
          </svg>
          <span id="docpickmodal-title">
            {target.key === 'custom' ? 'Add custom doc' : 'Choose documentation'}
          </span>
        </h2>
        <button className="btn" id="docpickmodal-close" onClick={close}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Close</span>
        </button>
      </div>

      <div className="perm-wrap">
        <div className="perm-inner" id="docpicker-view">
          <input
            className="fld"
            id="docpicker-search"
            placeholder="Search existing doc pages…"
            style={{ width: '100%', marginBottom: 14 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* CREATE COMES FIRST (V0.42.4). It used to sit under the results, and it is not
              one button but four controls — the title, the type, the URL an embedded or
              external page needs, and the button — so the whole block moved rather than the
              button alone. Collapsed until asked for, so the dialog stays short on the visits
              where you came to attach something that already exists. */}
          {!creating ? (
            <div
              className="docpick-createrow"
              id="docpicker-createrow"
              role="button"
              tabIndex={0}
              onClick={() => setCreating(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCreating(true);
                }
              }}
            >
              <svg className="ico" style={{ width: 15, height: 15 }}>
                <use href="#i-plus" />
              </svg>
              <span>Create a new document</span>
            </div>
          ) : (
            <div className="docpick-createbox">
              <input
                className="fld"
                id="docpicker-newtitle"
                placeholder="New doc page title..."
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              {/* What the new page will hold. Chosen, never inferred — guessing from the URL
                  host is what mis-read self-hosted Supademo domains in V0.21. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                <select
                  className="fld"
                  id="docpicker-newtype"
                  style={{ width: 'auto', minWidth: 170 }}
                  value={docType}
                  onChange={(e) => setDocType(e.target.value as DocType)}
                >
                  {DOC_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <span id="docpicker-newhint" style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>
                  {typeDef.hint}
                </span>
              </div>
              {docType !== 'standard' ? (
                <input
                  className="fld"
                  id="docpicker-newurl"
                  placeholder="https://..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              ) : null}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="pbtn"
                  id="docpicker-createcancel"
                  onClick={() => setCreating(false)}
                >
                  Cancel
                </button>
                <button className="pbtn primary" id="docpicker-create" onClick={create}>
                  Create new
                </button>
              </div>
            </div>
          )}

          <div id="docpicker-results">
            {failed ? (
              <div style={{ fontSize: 13, color: 'var(--ink-faint)' }}>Could not load doc pages.</div>
            ) : rows === null ? (
              <div style={{ fontSize: 13, color: 'var(--ink-faint)' }}>Loading…</div>
            ) : !rows.length ? (
              <div style={{ fontSize: 13, color: 'var(--ink-faint)' }}>No matching doc pages.</div>
            ) : (
              rows.map((d) =>
                confirmId === d.id ? (
                  /* THE ROW ASKS, rather than a dialog over it: what you are about to delete
                     stays on screen and the list behind it is never covered (V0.42.4). */
                  <div className="docpick-row confirming" key={d.id} data-confirm={d.id}>
                    <span className="docpick-confirmtext">
                      Delete &ldquo;{d.title}&rdquo; everywhere?{' '}
                      {usedBy(d.id) === 1 ? '1 node loses it.' : `${usedBy(d.id)} nodes lose it.`}
                    </span>
                    <button className="pbtn" onClick={() => setConfirmId(null)}>
                      Cancel
                    </button>
                    <button
                      className="pbtn docpick-del-go"
                      onClick={() => void remove(d.id)}
                    >
                      Delete
                    </button>
                  </div>
                ) : (
                  <div className="docpick-row" key={d.id}>
                    <svg className="ico" style={{ width: 15, height: 15 }}>
                      <use href={`#${docIconId(s.docTypes, d.id)}`} />
                    </svg>
                    <span className="title">{d.title}</span>
                    <span className="kind">{rowKind(d)}</span>
                    <button className="pbtn primary" onClick={() => attach(d.id)}>
                      Attach
                    </button>
                    {/* Edit reaches an EXISTING page, which several nodes may share — editing
                        it here changes it for all of them, as it should. */}
                    <button
                      className="pbtn"
                      onClick={() => {
                        close();
                        callAction('openDocEditor', d.id, target.nodeId);
                      }}
                    >
                      Edit
                    </button>
                    {mayDelete ? (
                      <button
                        className="docpick-del"
                        title="Delete this document"
                        aria-label={`Delete ${d.title}`}
                        onClick={() => setConfirmId(d.id)}
                      >
                        <svg className="ico" style={{ width: 14, height: 14 }}>
                          <use href="#i-trash" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                ),
              )
            )}
          </div>

          {/* A custom doc is one of a list, so there is no single attachment to remove. */}
          {target.key !== 'custom' ? (
            <button
              className="btn danger"
              id="docpicker-detach"
              style={{ marginTop: 14 }}
              onClick={detach}
            >
              Remove attached doc
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
